import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Archive, Heart, Plus, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * PO / Super Admin read-only roster of Faith Journeys.
 * Includes a serve-team catalog editor at the bottom (PO+ manages).
 * Never edits another user's journey (Super Admin has a separate audit-
 * logged endpoint accessible from the users page — not surfaced here to
 * keep this page a shepherded overview, not a data-entry surface).
 */
export default function FaithJourneysRoster() {
  const [data, setData] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [campus, setCampus] = useState("");
  const [role, setRole] = useState("");
  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState("");
  const [savingTeam, setSavingTeam] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (campus) params.campus_id = campus;
      if (role) params.role = role;
      const [r, t, c] = await Promise.all([
        api.get("/faith-journey/roster", { params }),
        api.get("/faith-journey/serve-teams?include_archived=true"),
        api.get("/campuses"),
      ]);
      setData(r.data);
      setTeams(t.data);
      setCampuses(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load the roster.");
    }
  };
  useEffect(() => { load(); }, [campus, role]);

  const addTeam = async () => {
    const name = newTeam.trim();
    if (!name) return;
    setSavingTeam(true);
    try {
      await api.post("/faith-journey/serve-teams", { name });
      setNewTeam("");
      toast.success("Team added.");
      const t = await api.get("/faith-journey/serve-teams?include_archived=true");
      setTeams(t.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSavingTeam(false); }
  };

  const archiveTeam = async (id) => {
    try {
      await api.post(`/faith-journey/serve-teams/${id}/archive`);
      toast.success("Team archived (historical memberships preserved).");
      const t = await api.get("/faith-journey/serve-teams?include_archived=true");
      setTeams(t.data);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const restoreTeam = async (id, currentName) => {
    try {
      await api.put(`/faith-journey/serve-teams/${id}`, { archived: false, name: currentName });
      toast.success("Team restored.");
      const t = await api.get("/faith-journey/serve-teams?include_archived=true");
      setTeams(t.data);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const summary = data?.summary || {};

  const stats = useMemo(() => ([
    { key: "baptized", label: "Baptized" },
    { key: "growth_track_completed", label: "Growth Track" },
    { key: "on_serve_team", label: "On A Serve Team" },
    { key: "in_neighborhood_group", label: "In A Group" },
    { key: "leading_a_group", label: "Leading A Group" },
    { key: "forward_completed", label: "FORWARD" },
    { key: "missions_trips_logged", label: "Missions Trips" },
  ]), []);

  return (
    <div className="space-y-6" data-testid="faith-journeys-roster-page">
      <section className="patria-card p-6">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
            <Heart className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
              People Operations · Read-Only
            </p>
            <h1 className="text-3xl font-serif text-forest-900">Faith Journeys Roster</h1>
            <p className="text-xs text-charcoal-muted mt-1 max-w-2xl">
              A warm overview of where each teammate and volunteer is on their journey with
              Patria. Read-only here — journeys are self-reported by each person.
            </p>
          </div>
        </div>
      </section>

      {/* Counts */}
      <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="fj-roster-summary">
        {stats.map((s) => (
          <div key={s.key} className="patria-card p-3 text-center">
            <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">{s.label}</p>
            <p className="mt-1 text-3xl font-serif text-forest-900" data-testid={`fj-summary-${s.key}`}>
              {summary[s.key] ?? 0}
            </p>
          </div>
        ))}
      </section>

      {/* Filters */}
      <section className="patria-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="patria-label">Campus</label>
          <select
            className="patria-input"
            value={campus}
            onChange={(e) => setCampus(e.target.value)}
            data-testid="fj-filter-campus"
          >
            <option value="">All Campuses</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="patria-label">User Type</label>
          <select
            className="patria-input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="fj-filter-role"
          >
            <option value="">Everyone</option>
            <option value="volunteer">Volunteers</option>
            <option value="employee">Employees</option>
            <option value="manager">Managers</option>
            <option value="po_admin">PO Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
      </section>

      {/* Roster table */}
      <section className="patria-card p-4 overflow-x-auto">
        <table className="patria-table w-full text-sm" data-testid="fj-roster-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Furthest Milestone</th>
              <th>Baptism</th>
              <th>Growth Track</th>
              <th>Serve Teams</th>
              <th>Neighborhood Group</th>
              <th>Leading</th>
              <th>FORWARD</th>
              <th>Trips</th>
            </tr>
          </thead>
          <tbody>
            {(!data || data.rows.length === 0) && (
              <tr>
                <td colSpan={10} className="italic text-charcoal-muted p-4 text-center">
                  No people match your filters.
                </td>
              </tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr key={r.user_id} data-testid={`fj-row-${r.user_id}`}>
                <td className="whitespace-nowrap">{r.user_name}</td>
                <td>
                  <span className={`patria-chip-${r.user_role === "volunteer" ? "terra" : "green"} text-[10px] capitalize`}>
                    {(r.user_role || "").replaceAll("_", " ")}
                  </span>
                </td>
                <td className="text-charcoal-soft">{r.furthest_milestone}</td>
                <td className="whitespace-nowrap">{r.baptism_date || "—"}</td>
                <td className="whitespace-nowrap">{r.growth_track_date || "—"}</td>
                <td className="text-xs">{r.serve_teams.join(", ") || "—"}</td>
                <td className="text-xs">{r.neighborhood_groups.join(", ") || "—"}</td>
                <td className="text-xs">{r.leading_groups.join(", ") || "—"}</td>
                <td className="whitespace-nowrap">{r.forward_date || "—"}</td>
                <td>{r.missions_trips_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Serve team catalog editor */}
      <section className="patria-card p-6 space-y-3" data-testid="fj-serve-teams-admin">
        <h2 className="text-2xl font-serif text-forest-900">Serve Team Catalog</h2>
        <p className="text-xs text-charcoal-muted italic">
          Add or archive teams. Archived teams keep their historical memberships and stop
          appearing in the join dropdown.
        </p>
        <ul className="space-y-2">
          {teams.map((t) => (
            <li key={t.id} className="p-3 rounded-xl bg-cream-100 flex items-center justify-between gap-2">
              <div>
                <p className={t.archived ? "text-charcoal-muted line-through" : "text-forest-900 font-medium"}>
                  {t.name}
                </p>
                {t.archived && (
                  <p className="text-[10px] uppercase tracking-widest text-terracotta-500">
                    Archived
                  </p>
                )}
              </div>
              {t.archived ? (
                <button
                  onClick={() => restoreTeam(t.id, t.name)}
                  className="patria-btn-ghost text-forest-700 text-xs"
                  data-testid={`fj-team-restore-${t.id}`}
                >
                  <Save className="h-4 w-4 mr-1" strokeWidth={1.5} /> Restore
                </button>
              ) : (
                <button
                  onClick={() => archiveTeam(t.id)}
                  className="patria-btn-ghost text-terracotta-600 text-xs"
                  data-testid={`fj-team-archive-${t.id}`}
                >
                  <Archive className="h-4 w-4 mr-1" strokeWidth={1.5} /> Archive
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2">
          <input
            className="patria-input flex-1"
            placeholder="Add A Team (e.g., Prayer Team)"
            value={newTeam}
            onChange={(e) => setNewTeam(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTeam(); } }}
            data-testid="fj-new-team-input"
          />
          <button
            type="button"
            onClick={addTeam}
            disabled={savingTeam || !newTeam.trim()}
            className="patria-btn-primary"
            data-testid="fj-new-team-btn"
          >
            <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Team
          </button>
        </div>
      </section>
    </div>
  );
}
